import React from 'react';

/**
 * Generic pagination footer used by every list page (Cases, Billing,
 * Inquiries, Mediators). Renders "Showing X-Y of Z" + prev / page-buttons
 * (with ellipsis when there are more than 7 pages) / next.
 *
 * Optionally renders a "Show" page-size dropdown on the left when
 * `onPageSizeChange` is supplied. Default options are [5, 10, 20, 50].
 *
 * Props:
 *   currentPage, totalPages, totalEntries, pageSize, onPageChange,
 *   onPageSizeChange? — when provided, renders the "Show N" select
 *   pageSizeOptions?  — defaults to [5, 10, 20, 50]
 */
export default function Pagination({
  currentPage,
  totalPages,
  totalEntries,
  pageSize = 10,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [5, 10, 20, 50],
}) {
  if (!totalEntries || totalEntries <= 0) return null;
  const fromIdx = (currentPage - 1) * pageSize + 1;
  const toIdx = Math.min(currentPage * pageSize, totalEntries);

  const pages = buildPageList(currentPage, totalPages);

  const setPage = (p) => {
    if (p < 1 || p > totalPages || p === currentPage) return;
    onPageChange(p);
  };

  // Page-size change always resets to page 1 — without this, a user on
  // page 4 of 10/page jumps to a page index that may not exist under the
  // new limit. Parent components don't need to remember to do it.
  const handlePageSizeChange = (e) => {
    const next = parseInt(e.target.value, 10);
    if (!Number.isFinite(next) || next === pageSize) return;
    if (typeof onPageChange === 'function') onPageChange(1);
    if (typeof onPageSizeChange === 'function') onPageSizeChange(next);
  };

  return (
    <div
      className="flex items-center justify-between bg-[#f9fafb] rounded-b-xl"
      style={{ height: 48, padding: '0 16px' }}
    >
      <div className="flex items-center gap-3">
        <span className="text-xs font-normal text-[#5b5a64]" style={{ fontFamily: 'Inter' }}>
          Showing {fromIdx}-{toIdx} of {totalEntries} entries
        </span>
        {typeof onPageSizeChange === 'function' && (
          <div className="flex items-center gap-1.5">
            <span
              className="text-xs font-normal text-[#5b5a64]"
              style={{ fontFamily: 'Inter' }}
            >
              Show
            </span>
            <div className="relative">
              <select
                value={pageSize}
                onChange={handlePageSizeChange}
                aria-label="Rows per page"
                className="appearance-none bg-white border border-[#e5e5e5] rounded-md text-xs font-medium text-[#0e1e20] cursor-pointer focus:outline-none focus:border-[#115e59]"
                style={{
                  fontFamily: 'Inter',
                  height: 28,
                  // Right padding leaves room for the chevron icon.
                  padding: '0 24px 0 8px',
                }}
              >
                {pageSizeOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
              <svg
                width="10"
                height="10"
                viewBox="0 0 14 14"
                fill="none"
                className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2"
              >
                <path
                  d="M3.5 5.25L7 8.75L10.5 5.25"
                  stroke="#5b5a64"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>
        )}
      </div>
      <div className="flex items-center gap-1">
        <button
          type="button"
          aria-label="Previous page"
          disabled={currentPage <= 1}
          onClick={() => setPage(currentPage - 1)}
          className="flex items-center justify-center w-7 h-7 rounded-md border border-[#e5e5e5] disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M8.75 3.5L5.25 7L8.75 10.5" stroke="#5b5a64" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        {pages.map((p, i) =>
          p === '…' ? (
            <span key={`gap-${i}`} className="text-xs text-[#5b5a64] px-0.5" style={{ fontFamily: 'Inter' }}>…</span>
          ) : (
            <button
              key={p}
              type="button"
              onClick={() => setPage(p)}
              className="flex items-center justify-center w-7 h-7 rounded-md text-xs font-medium"
              style={{
                fontFamily: 'Inter',
                backgroundColor: currentPage === p ? '#115e59' : 'transparent',
                color: currentPage === p ? '#fff' : '#0e1e20',
              }}
            >
              {p}
            </button>
          )
        )}
        <button
          type="button"
          aria-label="Next page"
          disabled={currentPage >= totalPages}
          onClick={() => setPage(currentPage + 1)}
          className="flex items-center justify-center w-7 h-7 rounded-md border border-[#e5e5e5] disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M5.25 3.5L8.75 7L5.25 10.5" stroke="#5b5a64" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
    </div>
  );
}

/**
 * Build the list of page tokens to render: numbers and `'…'` separators.
 * Always shows first/last page, the current page, and one neighbour either
 * side. Inserts an ellipsis when the gap between shown pages is > 1.
 */
function buildPageList(current, total) {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const set = new Set([1, total, current, current - 1, current + 1]);
  const sorted = [...set].filter((n) => n >= 1 && n <= total).sort((a, b) => a - b);
  const out = [];
  for (let i = 0; i < sorted.length; i++) {
    out.push(sorted[i]);
    if (i < sorted.length - 1 && sorted[i + 1] - sorted[i] > 1) {
      out.push('…');
    }
  }
  return out;
}
